import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Archive, Building2, ChevronRight, FolderOpen } from 'lucide-react';
import { EmptyState, PageHeader } from '@/shared/components';
import { useApi, type ObjectApartmentSummary, type PropertyGroupResponse } from '@/shared/api';
import { useObjectScope } from '@/shared/object-scope';

function ApartmentLink({ apartment }: { apartment: ObjectApartmentSummary }) {
  return (
    <Link
      to="/akte/$propertyId"
      params={{ propertyId: apartment.id }}
      className="border-ink-soft/15 hover:border-sage hover:bg-card flex items-center justify-between rounded-lg border px-4 py-3 transition-colors"
    >
      <span className="text-ink flex items-center gap-3 text-sm font-medium">
        <FolderOpen size={18} className="text-sage" aria-hidden />
        {apartment.name}
      </span>
      <ChevronRight size={18} className="text-ink-faint" aria-hidden />
    </Link>
  );
}

function ObjectAkteGroup({ group }: { group: PropertyGroupResponse }) {
  const active = group.apartments.filter((apartment) => apartment.status !== 'Archived');
  const archived = group.apartments.filter((apartment) => apartment.status === 'Archived');
  return (
    <section className="bg-card border-ink-soft/15 rounded-xl border p-5">
      <div className="mb-4 flex items-start gap-3">
        <Building2 size={20} className="text-sealwax mt-0.5" aria-hidden />
        <div>
          <h2 className="text-ink font-serif text-xl">{group.name}</h2>
          <p className="text-ink-soft mt-1 text-sm">
            {group.address.street}, {group.address.postalCode} {group.address.city}
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {active.map((apartment) => (
          <ApartmentLink key={apartment.id} apartment={apartment} />
        ))}
      </div>
      {archived.length > 0 && (
        <div className="border-ink-soft/15 mt-4 border-t pt-4">
          <p className="text-ink-soft mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
            <Archive size={14} aria-hidden /> Archivierte Apartments
          </p>
          <div className="space-y-2">
            {archived.map((apartment) => (
              <ApartmentLink key={apartment.id} apartment={apartment} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function WorkspaceAkteNavigator() {
  const api = useApi();
  const { groupId } = useObjectScope();
  const groups = useQuery({
    queryKey: ['property-groups'],
    queryFn: api.propertyGroups.list,
  });
  const visible = (groups.data ?? []).filter((group) => !groupId || group.id === groupId);
  return (
    <>
      <PageHeader
        eyebrow="Immobilien"
        title="Ihre"
        titleAccent="Akte"
        description="Wählen Sie ein Objekt und anschließend das Apartment mit seiner digitalen Akte."
      />
      {groups.error ? (
        <EmptyState
          icon="Error"
          title="Akte konnte nicht geladen werden"
          description="Bitte Verbindung prüfen und erneut versuchen."
        />
      ) : groups.isLoading ? (
        <p className="text-ink-soft" role="status">
          Aktenübersicht wird geladen …
        </p>
      ) : visible.length === 0 ? (
        <EmptyState
          icon="Building"
          title="Keine zugänglichen Apartments"
          description="Der aktuelle Objektkontext enthält keine Apartments mit digitaler Akte."
        />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {visible.map((group) => (
            <ObjectAkteGroup key={group.id} group={group} />
          ))}
        </div>
      )}
    </>
  );
}
